import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthContext";
import { checkIfUserIsRole } from "../../utils/authUtils";
import LoadingScreen from "../LoadingScreen";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: string;
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, isInitializing, getToken } = useAuth();
  const [hasRole, setHasRole] = useState<boolean | null>(null);

  // once auth status is known, grab token & check role
  useEffect(() => {
    if (!isAuthenticated) {
      setHasRole(false);
      return;
    }
    getToken()
      .then((token) => {
        setHasRole(checkIfUserIsRole(token, requiredRole));
      })
      .catch(() => {
        setHasRole(false);
      });
  }, [isAuthenticated, getToken, requiredRole]);

  // still bootstrapping
  if (isInitializing || hasRole === null) {
    return <LoadingScreen>{null}</LoadingScreen>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // wrong role → home
  if (!hasRole) {
    return <Navigate to="/" replace />;
  }

  // Render children if authenticated and has required role
  return <>{children}</>;
};

export default RoleProtectedRoute;
